import { parsePayload, parseSlipVerify, parseTrueMoneySlipVerify } from '@thai-qr-payment/payload';
import type { Category, PaymentMethod, Transaction } from '../domain/types';
import type { QrClassification, ScanProposal } from './types';

export const SCANNER_PARSER_VERSION = 'thai-slip-1';

const bankAliases: Record<string, string[]> = {
  '002': ['bangkok bank', 'bbl', 'กรุงเทพ'],
  '004': ['kasikorn', 'kbank', 'กสิกร'],
  '006': ['krungthai', 'ktb', 'กรุงไทย'],
  '011': ['ttb', 'tmb', 'ทหารไทยธนชาต'],
  '014': ['scb', 'siam commercial', 'ไทยพาณิชย์'],
  '025': ['krungsri', 'bank of ayudhya', 'กรุงศรี'],
  '030': ['gsb', 'government savings', 'ออมสิน'],
  '033': ['ghb', 'government housing', 'อาคารสงเคราะห์'],
  '034': ['baac', 'ธกส'],
  '067': ['tisco', 'ทิสโก้'],
  '069': ['kiatnakin', 'kkp', 'เกียรตินาคิน'],
};

const thaiMonths: Record<string, number> = {
  'ม.ค.': 1, 'มกราคม': 1, 'ก.พ.': 2, 'กุมภาพันธ์': 2,
  'มี.ค.': 3, 'มีนาคม': 3, 'เม.ย.': 4, 'เมษายน': 4, 'พ.ค.': 5, 'พฤษภาคม': 5,
  'มิ.ย.': 6, 'มิถุนายน': 6, 'ก.ค.': 7, 'กรกฎาคม': 7, 'ส.ค.': 8, 'สิงหาคม': 8,
  'ก.ย.': 9, 'กันยายน': 9, 'ต.ค.': 10, 'ตุลาคม': 10, 'พ.ย.': 11, 'พฤศจิกายน': 11,
  'ธ.ค.': 12, 'ธันวาคม': 12,
};

const englishMonths: Record<string, number> = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8,
  sep: 9, sept: 9, september: 9, oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12,
};

function validDate(year: number, month: number, day: number): string | undefined {
  if (year >= 2400) year -= 543;
  if (year < 100) year += year <= 69 ? 2000 : 1900;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return undefined;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function extractDate(text: string): string | undefined {
  const iso = text.match(/\b(20\d{2}|25\d{2})[-/.](\d{1,2})[-/.](\d{1,2})\b/);
  if (iso) return validDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  const numeric = text.match(/\b(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})\b/);
  if (numeric) return validDate(Number(numeric[3]), Number(numeric[2]), Number(numeric[1]));
  const monthNames = { ...thaiMonths, ...englishMonths };
  for (const [name, month] of Object.entries(monthNames)) {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = text.match(new RegExp(`(?:^|\\s)(\\d{1,2})\\s*${escaped}\\s*(\\d{2,4})(?:\\s|$)`, 'i'));
    if (match) return validDate(Number(match[2]), month, Number(match[1]));
  }
  return undefined;
}

export function extractAmount(text: string): string | undefined {
  const candidates: Array<{ value: string; score: number }> = [];
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    const lower = line.toLowerCase();
    const context = /(amount|total|paid|transfer|ยอด|จำนวนเงิน|รวม|โอนเงิน|ชำระ)/i.test(line);
    const currency = /(thb|บาท|฿)/i.test(line);
    const penalty = /(balance|account|เลขที่|reference|ref|fee|ค่าธรรมเนียม)/i.test(line) ? 3 : 0;
    for (const match of line.matchAll(/(?:฿|thb\s*)?((?:\d{1,3}(?:,\d{3})+|\d+)(?:[.,]\d{2})?)(?:\s*(?:thb|บาท))?/gi)) {
      const normalized = match[1].replace(/,/g, '');
      const number = Number(normalized);
      if (!Number.isFinite(number) || number <= 0 || number > 90_000_000) continue;
      const hasDecimals = /[.,]\d{2}$/.test(match[1]);
      candidates.push({ value: number.toFixed(hasDecimals ? 2 : 0), score: (context ? 5 : 0) + (currency ? 4 : 0) + (hasDecimals ? 1 : 0) - penalty });
    }
    if (lower.includes('qr')) continue;
  }
  candidates.sort((a, b) => b.score - a.score || Number(b.value) - Number(a.value));
  return candidates[0] && candidates[0].score >= 4 ? candidates[0].value : undefined;
}

function cleanName(value: string): string {
  return value.replace(/^[\s:：-]+/, '').replace(/\s{2,}/g, ' ').trim().slice(0, 200);
}

export function extractMerchant(text: string): string | undefined {
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const labelled = /^(?:to|recipient|merchant|payee|ผู้รับเงิน|ผู้รับ|ไปยัง|ชื่อบัญชี|ร้านค้า)\s*[:：-]?\s*(.+)$/i;
  for (const line of lines) {
    const match = line.match(labelled);
    if (match && !/^(?:account|เลขที่|promptpay|พร้อมเพย์|xxx|\d[\d x*-]+)$/i.test(match[1])) return cleanName(match[1]);
  }
  return undefined;
}

function bankFromText(text: string): string | undefined {
  const lower = text.toLowerCase();
  for (const [code, aliases] of Object.entries(bankAliases)) if (aliases.some((alias) => lower.includes(alias))) return code;
  if (/truemoney|true money|ทรูมันนี่/i.test(text)) return 'truemoney';
  return undefined;
}

function lastFourFromText(text: string): string | undefined {
  const relevant = text.split(/\r?\n/).find((line) => /(from|account|card|wallet|จาก|บัญชี|บัตร)/i.test(line) && /\d/.test(line));
  const digits = relevant?.replace(/\D/g, '');
  return digits && digits.length >= 4 ? digits.slice(-4) : undefined;
}

export function parseQr(qrText?: string): Pick<ScanProposal, 'qrClassification' | 'sourceIdentity' | 'bankHint' | 'referenceHint' | 'amount' | 'merchant' | 'eventDate' | 'warnings'> {
  const base = { qrClassification: 'none' as QrClassification, warnings: [] as string[] };
  if (!qrText) return base;
  const slip = parseSlipVerify(qrText);
  if (slip) return { ...base, qrClassification: 'slip_reference', bankHint: slip.sendingBank, referenceHint: slip.transRef.slice(-6), sourceIdentity: `slip:${slip.sendingBank}:${slip.transRef}` };
  const trueMoney = parseTrueMoneySlipVerify(qrText);
  if (trueMoney) {
    const date = trueMoney.date.match(/^(\d{2})(\d{2})(\d{4})$/);
    return { ...base, qrClassification: 'slip_reference', bankHint: 'truemoney', referenceHint: trueMoney.transactionId.slice(-6), sourceIdentity: `truemoney:${trueMoney.transactionId}`, eventDate: date ? validDate(Number(date[3]), Number(date[2]), Number(date[1])) : undefined };
  }
  try {
    const payload = parsePayload(qrText, { strict: true });
    if (payload.country === 'TH') return {
      ...base,
      qrClassification: 'merchant_payment_request',
      amount: payload.amount == null ? undefined : payload.amount.toFixed(2),
      merchant: payload.merchantName?.trim() || undefined,
      warnings: ['This QR requests a payment; it does not prove that a payment was completed.'],
    };
  } catch { /* Not a valid Thai merchant-payment QR. */ }
  return { ...base, qrClassification: 'unknown', warnings: ['A QR code was found, but Money Journal could not classify it. It was not opened.'] };
}

export function parseDocumentText(text: string, qrText?: string): ScanProposal {
  const qr = parseQr(qrText);
  const amount = qr.amount ?? extractAmount(text);
  const eventDate = qr.eventDate ?? extractDate(text);
  const merchant = qr.merchant ?? extractMerchant(text);
  return {
    amount,
    eventDate,
    merchant,
    bankHint: qr.bankHint ?? bankFromText(text),
    lastFourHint: lastFourFromText(text),
    referenceHint: qr.referenceHint,
    qrClassification: qr.qrClassification,
    sourceIdentity: qr.sourceIdentity,
    text,
    warnings: qr.warnings,
  };
}

export function suggestPaymentMethod(methods: PaymentMethod[], proposal: ScanProposal): PaymentMethod | undefined {
  const aliases = proposal.bankHint === 'truemoney' ? ['truemoney', 'true money', 'ทรูมันนี่'] : bankAliases[proposal.bankHint ?? ''] ?? [];
  return methods
    .map((method) => ({ method, score: (proposal.lastFourHint && method.lastFour === proposal.lastFourHint ? 8 : 0) + (aliases.some((alias) => method.label.toLowerCase().includes(alias)) ? 5 : 0) + (proposal.bankHint === 'truemoney' && method.type === 'e_wallet' ? 2 : 0) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)[0]?.method;
}

export function suggestCategory(categories: Category[], transactions: Transaction[], merchant?: string): Category | undefined {
  const normalized = merchant?.toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  if (normalized) {
    const counts = new Map<string, number>();
    for (const tx of transactions) {
      if (tx.deletedAt || tx.type === 'transfer' || tx.merchant.toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim() !== normalized) continue;
      for (const allocation of tx.allocations) counts.set(allocation.categoryId, (counts.get(allocation.categoryId) ?? 0) + 1);
    }
    const categoryId = [...counts].sort((a, b) => b[1] - a[1])[0]?.[0];
    const historyMatch = categories.find((category) => category.id === categoryId && !category.archived);
    if (historyMatch) return historyMatch;
  }
  const haystack = normalized ?? '';
  const keywords: Array<[RegExp, RegExp]> = [
    [/(food|grocer|restaurant|cafe|coffee|market|อาหาร|ร้าน|กาแฟ|โลตัส|บิ๊กซี|grab)/i, /(food|grocer|dining|อาหาร)/i],
    [/(taxi|transport|bts|mrt|fuel|gas|เดินทาง|น้ำมัน)/i, /(transport|travel|เดินทาง)/i],
    [/(rent|condo|electric|water|internet|utility|ค่าเช่า|ไฟฟ้า|ประปา)/i, /(home|housing|bill|บ้าน|ที่พัก)/i],
  ];
  for (const [merchantPattern, categoryPattern] of keywords) if (merchantPattern.test(haystack)) {
    const match = categories.find((category) => !category.archived && categoryPattern.test(category.label));
    if (match) return match;
  }
  return undefined;
}
